// "Upload stands alone" (flag REACT_APP_UPLOAD_PENDING_ENABLED, design 14/9
// §5.4) - the pure schedule and the on-demand gate of the editor's re-read
// while the API's worker still owes an UPLOAD its supplier identity. Pure
// module: no flag reads, no timers, so it can be unit-tested without React.

// Re-read schedule while the upload is worker-owned: 2, 2, 3, 3 s between
// calls - 4 calls, 10 s in total. The worker lands p50 ~2.4-3 s after the
// supplier answers (Upload_Base64 ~1.5 s + hd ~0.7 s + queue 0.3 s), so the
// second call normally carries the landing when the supplier is up; when it
// is down the badge simply stays after the fourth call and the on-demand
// gate below (or a new view) takes over - never an endless poll.
export const UPLOAD_KIND_POLL_MS: readonly number[] = [2_000, 2_000, 3_000, 3_000];

// On-demand re-read when the user touches a locked control (colour, method,
// size, Save & approve): at most ONE call per 10 s and at most 2 per logo
// per editor session, so a user waiting through a long supplier outage
// never generates more than 4 + 2 = 6 GetLogo from one editor.
export const UPLOAD_KIND_ON_DEMAND_THROTTLE_MS = 10_000;
export const UPLOAD_KIND_ON_DEMAND_MAX = 2;

// The delay before re-read number `attempt` (0-based), or undefined when
// the budget is spent.
export function uploadKindPollDelay(
  attempt: number,
  delays: readonly number[] = UPLOAD_KIND_POLL_MS
): number | undefined {
  if (!Number.isInteger(attempt) || attempt < 0) return undefined;
  if (attempt >= delays.length) return undefined;
  return delays[attempt];
}

export interface OnDemandReadState {
  // The logo the counters belong to; another logo starts over.
  logoId: number;
  count: number;
  // Date.now() of the last on-demand call (0 = none yet).
  lastAt: number;
}

export function initialOnDemandReadState(): OnDemandReadState {
  return { logoId: 0, count: 0, lastAt: 0 };
}

/**
 * The state after an on-demand re-read for `logoId` at `now`, or undefined
 * when none may be fired: the per-logo cap is reached, or the last call is
 * younger than the throttle window. A different logo id resets the counters
 * (a new logo in the same tool = a new budget).
 */
export function nextOnDemandRead(
  state: OnDemandReadState,
  logoId: number,
  now: number,
  options: { throttleMs?: number; max?: number } = {}
): OnDemandReadState | undefined {
  const throttleMs = options.throttleMs ?? UPLOAD_KIND_ON_DEMAND_THROTTLE_MS;
  const max = options.max ?? UPLOAD_KIND_ON_DEMAND_MAX;
  if (!(logoId > 0)) return undefined;
  const current =
    state.logoId === logoId ? state : { logoId, count: 0, lastAt: 0 };
  if (current.count >= max) return undefined;
  if (current.lastAt > 0 && now - current.lastAt < throttleMs) return undefined;
  return { logoId, count: current.count + 1, lastAt: now };
}
